import { autocompleteClasses } from "@mui/material";
import { alpha, createTheme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";
import CaretIcon from "components/Icons/CaretIcon";
import SvgIcon from "@mui/material/SvgIcon";
import CloseIcon from "components/Icons/CloseIcon";

const paletteColors = {
  text: {
    primary: "#000",
    secondary: "#4D4D4D",
  },
  common: {
    white: "#FFFFFF",
  },
  primary: {
    main: "#000",
    contrastText: "#FFFFFF",
  },
  /**
   * Secondary - For positive connotations UI elements and backgrounds(e.g: target achieved, progress made...)
   */
  secondary1: {
    180: "#0A604B",
    100: "#3DC4A3",
    60: "#92FFDD",
    10: "#E9FFF8",
  },
  /**
   * Secondary2 - For negative connotations UI elements and backgrounds (e.g: regression in progress..)
   */
  secondary2: {
    180: "#6B001A",
    100: "#E02C57",
    60: "#FF8CA8",
    40: "#FA7365",
    10: "#FFE4EA",
  },
  /**
   * Secondary3 - For notes on exercises and workouts
   */
  secondary3: {
    160: "#987A00",
    100: "#F1C40F",
    20: "#FCF3CF",
  },
  /**
   * Secondary4 - For video steps in workouts
   */
  secondary4: {
    100: "#6665DD",
    20: "#E0E0F8",
  },
  /**
   * System Feedback Colours
   */
  error: {
    main: "#C0392B",
    light: "#FFDBD7",
    160: "#79261F",
  },
  warning: {
    main: "#E67E22",
    light: "#FFE7D2",
  },
  success: {
    dark: "#018B51",
    main: "#04B86C",
    light: "#CCFFEA",
  },
  info: {
    main: "#2980B9",
    light: "#D7EFFF",
  },
  danger: {
    /** error.main */
    main: "#C0392B",
  },
  /**
   * Grey - For neutral UI elements and backgrounds (e.g: no progress...)
   */
  grey: {
    10: "#FAFBFE",
    20: "#EEF1F8",
    40: "#DCE2E9",
    60: "#BDC4CC",
    100: "#828990",
    120: "#676D73",
    160: "#383C3F",
  },
};
/**
 * @description New theme palette structure
 */
const theme = createTheme({
  palette: {
    mode: "light",
    background: {
      default: paletteColors.grey[10],
    },
    ...paletteColors,
    /**
     * @deprecated Used internally
     */
    secondary: {
      main: paletteColors.secondary1[100],
    },
    divider: paletteColors.grey[20],
    action: {
      // active: paletteColors.grey[100],
      hover: paletteColors.grey[20],
      selected: paletteColors.primary.main,
      // disabled: paletteColors.grey[10],
      // disabledBackground: paletteColors.grey[10],
    },
  },
  spacing: 8,
  shape: {
    /** @property {borderRadiusSmall: 4} */
    borderRadiusSmall: 4,
    /** @property {borderRadius: 10} */
    borderRadius: 10,
    /** @property {borderRadiusLarge: 20} */
    borderRadiusLarge: 20,
  },
  /**
   * @description New shadows design
   *
   * - None
   * - Box Shadow Default
   * - Box Shadow Hover
   * - Box Shadow Modal
   * - Box Shadow Small Element
   * - Box Shadow Floating Element
   */
  shadows: [
    "none",
    /**
     * Box Shadow Default @index 1
     */
    "2px 2px 10px 0px rgba(0, 0, 0, 0.08)",
    /**
     * Box Shadow Hover @index 2
     */
    "2px 2px 14px 0px rgba(0, 0, 0, 0.20)",
    /**
     * Box Shadow Modal @index 3
     */
    "4px 4px 20px 0px rgba(0, 0, 0, 0.12)",
    /**
     * Box Shadow Small Element @index 4
     */
    "2px 2px 10px 0px rgba(0, 0, 0, 0.16)",
    /**
     * Box Shadow Floating Element @index 5
     */
    "0px 2px 10px 0px rgba(0, 0, 0, 0.25)",
    /**
     * Box Shadow Top Navigation @index 6
     */
    "4px 4px 20px 0px rgba(0, 0, 0, 0.05)",
    /**
     * Box Shadow Drawer @index 7
     */
    "-4px 0px 46px 0px rgba(0, 0, 0, 0.04)",
    /**
     * Box Shadow Floating Buttons @index 8
     */
    "0 2px 10px 0 rgba(0, 0, 0, 0.25)",
    ...Array(18).fill("2px 2px 10px 0px rgba(0, 0, 0, 0.08)"),
  ] as Shadows,
  transitions: { duration: { standard: 200 } },
  typography: {
    fontFamily: ["Roboto", "-apple-system", "BlinkMacSystemFont", "sans-serif"].join(", "),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    /* Heading XXL 64px */
    h1: {
      fontSize: 64,
      fontWeight: 500,
      lineHeight: "70px" /* 109.375% */,
    },
    /* Heading XL 48px */
    h2: {
      fontSize: 48,
      fontWeight: 500,
      lineHeight: "54px" /* 112.5% */,
    },
    /* Heading L 40px */
    h3: {
      fontSize: 40,
      fontWeight: 500,
      lineHeight: "46px" /* 115% */,
    },
    /* Heading M 36px */
    h4: {
      fontSize: 36,
      fontWeight: 500,
      lineHeight: "42px" /* 116.667% */,
    },
    /* Heading S 32px */
    h5: {
      fontSize: 32,
      fontWeight: 500,
      lineHeight: "38px" /* 118.75% */,
    },
    /* Text XXL 24px - Bold */
    h6: {
      fontSize: 24,
      fontWeight: 500,
      lineHeight: "30px" /* 118.75% */,
    },
    /* Text XL 20px */
    subtitle1: {
      fontSize: 20,
      lineHeight: "26px" /* 130% */,
    },
    /* Text L 18px */
    subtitle2: {
      fontSize: 18,
      lineHeight: "24px" /* 133.33% */,
    },
    /* Text M 16px */
    body1: {
      fontSize: 16,
      lineHeight: "22px",
    },
    /* Text S 14px */
    body2: {
      fontSize: 14,
      lineHeight: "20px",
    },
    /* Text XS 12px */
    caption: {
      fontSize: 12,
      lineHeight: "18px",
    },
    button: {
      fontSize: 16,
      lineHeight: "22px",
      fontWeight: 500,
    },
    /**
     * Custom variants
     */
    /** Design token "Caption Text 10px". */
    Caption: {
      fontSize: 10,
      lineHeight: "16px",
      fontWeight: 500,
    },
    /** Design token "Heading XS 28px". The 24px bold style is `TextXXL` with a medium weight. */
    HeadingXS: {
      fontSize: 28,
      lineHeight: "30px",
      fontWeight: 500,
    },
    HeadingM: {
      fontSize: 36,
      lineHeight: "42px",
      fontWeight: 500,
    },
    /** Design token "Heading XL 48px" — the largest heading step in use. */
    HeadingXL: {
      fontSize: 48,
      lineHeight: "54px",
      fontWeight: 500,
    },
    TextXS: {
      fontSize: 12,
      lineHeight: "18px",
    },
    TextS: {
      fontSize: 14,
      lineHeight: "20px",
    },
    TextM: {
      fontSize: 16,
      lineHeight: "22px",
    },
    TextL: {
      fontSize: 18,
      lineHeight: "24px",
    },
    TextXL: {
      fontSize: 20,
      lineHeight: "26px",
    },
    TextXXL: {
      fontSize: 24,
      lineHeight: "30px",
    },
  },
  // // V5 overrides
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          Caption: "p",
          TextXS: "p",
          TextS: "p",
          TextM: "p",
          TextL: "p",
          TextXL: "p",
          TextXXL: "p",
          // Heading
          HeadingXS: "h1",
          HeadingM: "h1",
          HeadingXL: "h1",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&:hover:not([aria-selected='true'])": {
            color: paletteColors.primary.main,
          },
          '&[aria-selected="true"]': {
            backgroundColor: paletteColors.primary.main,
            color: paletteColors.common.white,
          },
          '&[aria-selected="true"]:hover': {
            backgroundColor: paletteColors.primary.main,
            color: paletteColors.common.white,
          },
          "&.MuiButtonBase-root.Mui-focusVisible": {
            backgroundColor: paletteColors.primary.main,
            color: paletteColors.common.white,
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          "&:hover": {
            backgroundColor: alpha(theme.palette.grey[60], 0.2),
          },
          "&.Mui-disabled": {
            color: theme.palette.grey[60],
          },
        }),
        sizeExtraSmall: ({ theme }) => ({
          width: theme.spacing(2.5),
          height: theme.spacing(2.5),
          padding: "4px",
          margin: 0,
          fontSize: "1.125rem",
        }),
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <CaretIcon color="primary" />,
        clearIcon: <CloseIcon sx={{ color: "grey.120" }} />,
      },
      styleOverrides: {
        groupLabel: ({ theme }) => ({
          color: theme.palette.grey[100] + "!important",
          lineHeight: "18px",
          fontSize: "12px",
          fontWeight: 400,
          padding: theme.spacing(0.5, 1.5),
        }),
        paper: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
        popupIndicator: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
        clearIndicator: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
        popper: ({ theme }) => ({
          [`& .${autocompleteClasses.groupLabel}`]: { color: theme.palette.primary.main },
          [`& .${autocompleteClasses.listbox}`]: {
            "&::-webkit-scrollbar": {
              width: "16px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: "16px",
              border: "4px solid transparent",
              backgroundColor: theme.palette.grey[60],
              backgroundClip: "padding-box",
              minHeight: "30px",
            },
            [`& .${autocompleteClasses.option}`]: {
              fontSize: "14px",
              lineHeight: "20px",
              padding: theme.spacing(1, 1.5),
              color: theme.palette.primary.main,
              "&.Mui-focused": {
                color: theme.palette.primary.main,
              },
              '&[aria-selected="true"]': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                // if selected and disabled color should the same as selected
                opacity: 1,
              },
              '&[aria-selected="true"].Mui-focused': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
              },
            },
          },
        }),
        endAdornment: {
          lineHeight: "16px",
          svg: {
            fontSize: "24px",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: (props) => <CaretIcon {...props} fontSize="small" />,
      },
      styleOverrides: {
        icon: {
          color: paletteColors.primary.main,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: paletteColors.grey[100],
        },
      },
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: (
          <SvgIcon sx={{ height: 16, width: 16, p: 0.25 }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 3L3 9" stroke="#676D73" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 3L9 9" stroke="#676D73" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </SvgIcon>
        ),
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
        slotProps: {
          select: {
            IconComponent: (props) => <CaretIcon {...props} color="primary" />,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeLarge: {
          fontSize: "2rem",
        },
        fontSizeMedium: {
          fontSize: "1.5rem",
        },
        fontSizeSmall: {
          fontSize: "1rem",
        },
        fontSizeExtraSmall: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          boxShadow: theme.shadows[4],
        }),
      },
    },
  },
});

export default theme;
